import { AltiusRes, INotificationsGroup } from "store/types";
import {
  IDeleteNotificationsGroupData,
  INotification,
  INotificationsRes,
  IUpdateNotificationsGroupData
} from "../../types";
import { UserAction } from "../actions";
import { AsyncActions, AsyncActionsParams } from "../base";

export interface INotificationsActions {
  getNotificationsAction: (page?: number) => Promise<void>;
  getDealNotificationsAction: (dealId: number, page?: number, per_page?: number) => Promise<INotification[]>;
  updateNotificationsAction: (id: number, status: string) => Promise<void>;
  deleteNotificationAction: (id: number) => Promise<void>;
  updateNotificationGroupAction: (data: IUpdateNotificationsGroupData) => Promise<void>;
  updateAllNotificationGroupAction: (data: IUpdateNotificationsGroupData) => Promise<void>;
  deleteNotificationGroupAction: (data: IDeleteNotificationsGroupData) => Promise<void>;
  clearCounter: () => Promise<void>;
  setFilterByDeal: (value: boolean) => void;
}

interface UserActionParams extends AsyncActionsParams {
  notificationsPage: number;
  dealId: number | undefined;
}

export class NotificationsActions extends AsyncActions implements INotificationsActions {
  protected readonly notificationsPage: number;
  protected readonly dealId: number | undefined;
  constructor({ dispatch, api, notificationsPage, dealId }: UserActionParams) {
    super({ api, dispatch });
    this.notificationsPage = notificationsPage;
    this.dealId = dealId;
  }

  public getNotificationsAction = async (page?: number): Promise<void> => {
    const {
      data: {
        data: { data }
      }
    } = await this.api.get<
      AltiusRes<{
        data: Array<INotification | INotificationsGroup>;
        total: number;
        last_page: number;
        per_page: number;
        current_page: number;
      }>
    >({
      url: "/api/v0.0.2/notifications",
      params: { page: page || this.notificationsPage, deal_id: this.dealId }
    });

    this.dispatch({
      type: UserAction.GetNotificationsSuccess,
      payload: data
    });
  };

  public getDealNotificationsAction = async (
    deal_id: number,
    page = 1,
    per_page = 5,
    for_deal_dashboard = true
  ): Promise<INotification[]> => {
    const {
      data: {
        data: { data }
      }
    } = await this.api.get<AltiusRes<INotificationsRes>>({
      url: "/api/v0.0.2/notifications",
      params: { page, deal_id, per_page, for_deal_dashboard }
    });
    return data;
  };

  public updateNotificationsAction = async (id: number, status: string): Promise<void> => {
    await this.api.patch({
      url: `/api/v0.0.2/notifications/${id}`,
      data: { status }
    });
    this.dispatch({
      type: UserAction.UpdateNotificationSuccess,
      payload: { id, status }
    });
  };

  public deleteNotificationAction = async (id: number): Promise<void> => {
    await this.api.delete({ url: `/api/v0.0.2/notifications/${id}` });
    this.dispatch({
      type: UserAction.DeleteNotificationSuccess,
      payload: id
    });
  };

  public updateNotificationGroupAction = async (data: IUpdateNotificationsGroupData): Promise<void> => {
    const { ids, status, elementIndex } = data;
    await this.api.patch({
      url: "/api/v0.0.2/group-notifications",
      data: { status, ids: [ids] }
    });
    this.dispatch({
      type: UserAction.UpdateNotificationsGroupSuccess,
      payload: { elementIndex, status }
    });
  };

  public updateAllNotificationGroupAction = async (data: IUpdateNotificationsGroupData): Promise<void> => {
    const { status } = data;
    await this.api.patch({
      url: "/api/v0.0.2/group-notifications",
      data: { status, ids: [] }
    });
    this.dispatch({
      type: UserAction.UpdateAllNotificationsGroupSuccess,
      payload: { status }
    });
  };

  public deleteNotificationGroupAction = async (data: IDeleteNotificationsGroupData): Promise<void> => {
    const { ids, elementIndex } = data;
    await this.api.delete({ url: `/api/v0.0.2/group-notifications/${ids}` });
    this.dispatch({
      type: UserAction.DeleteNotificationsGroupSuccess,
      payload: elementIndex
    });
  };

  public clearCounter: () => Promise<void> = async (): Promise<void> => {
    await this.api.post({
      url: "/api/v0.0.2/notifications/clear-count"
    });
  };

  public setFilterByDeal = (payload: boolean): void => {
    this.dispatch({ type: UserAction.SetFilterByDeal, payload });
  };
}
